import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CookieBanner from '../../components/feature/CookieBanner';
import { useSEO, getServiceSchema, getBreadcrumbSchema } from '../../utils/seo';

const ServiciosPage = () => {
  const [activeService, setActiveService] = useState(0);

  useSEO({
    title: 'Servicios de Limpieza Post Mortem Barcelona | BIOTRAUMA',
    description: 'Servicios especializados de limpieza post mortem, desinfección profesional, neutralización de olores y retirada de enseres en Barcelona. Presupuesto sin compromiso.',
    keywords: 'servicios limpieza post mortem, desinfección profesional, neutralización olores, retirada enseres, Barcelona',
    canonical: '/servicios',
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        getServiceSchema(),
        getBreadcrumbSchema([
          { name: 'Inicio', url: '/' },
          { name: 'Servicios', url: '/servicios' }
        ])
      ]
    }
  });

  const services = [
    {
      id: 'limpieza-post-mortem',
      icon: 'ri-shield-check-line',
      title: 'Limpieza Post Mortem',
      subtitle: 'Intervención especializada tras fallecimiento',
      price: 'Desde 450€',
      description: 'Servicio completo de limpieza especializada tras fallecimiento en el inmueble. Realizamos una intervención profesional con protocolos específicos, equipos de protección individual certificados y productos homologados para garantizar la correcta desinfección y saneamiento del espacio.',
      includes: [
        'Evaluación inicial del estado del inmueble',
        'Limpieza profunda de todas las superficies afectadas',
        'Desinfección completa con productos homologados',
        'Retirada y gestión de residuos biológicos',
        'Documentación fotográfica del proceso',
        'Certificado de intervención realizada'
      ],
      cases: [
        'Fallecimiento natural en domicilio con tiempo de permanencia',
        'Situaciones que requieren limpieza especializada',
        'Preparación del inmueble tras el levantamiento del cuerpo',
        'Coordinación con servicios funerarios y familia'
      ],
      timing: 'Entre 4 y 8 horas según el estado del inmueble y la superficie afectada. Podemos realizar la intervención en horario adaptado a sus necesidades.',
      process: [
        'Evaluación inicial y determinación del alcance',
        'Preparación del espacio y protección de zonas no afectadas',
        'Limpieza y desinfección exhaustiva',
        'Verificación final y entrega de documentación'
      ]
    },
    {
      id: 'desinfeccion-saneamiento',
      icon: 'ri-virus-line',
      title: 'Desinfección y Saneamiento',
      subtitle: 'Desinfección profesional según normativa',
      price: 'Desde 350€',
      description: 'Desinfección profesional del inmueble según normativa sanitaria vigente, eliminando cualquier riesgo biológico. Utilizamos productos homologados y técnicas certificadas que garantizan la eliminación de patógenos y la seguridad del espacio.',
      includes: [
        'Desinfección con productos homologados',
        'Aplicación de técnicas certificadas',
        'Cumplimiento de normativa sanitaria vigente',
        'Tratamiento de superficies porosas y no porosas',
        'Verificación de la efectividad del tratamiento',
        'Certificado oficial de desinfección'
      ],
      cases: [
        'Desinfección tras limpieza post mortem',
        'Saneamiento para entrega a nuevos inquilinos',
        'Requisito de compañías de seguros',
        'Preparación para venta o alquiler del inmueble'
      ],
      timing: 'Entre 2 y 4 horas según la superficie del inmueble. El espacio debe permanecer ventilado durante 2-4 horas adicionales tras la intervención.',
      process: [
        'Preparación del espacio y protección de elementos sensibles',
        'Aplicación de desinfectantes homologados',
        'Tiempo de actuación según producto utilizado',
        'Ventilación controlada y verificación final'
      ]
    },
    {
      id: 'neutralizacion-olores',
      icon: 'ri-wind-line',
      title: 'Neutralización de Olores',
      subtitle: 'Eliminación completa mediante técnicas especializadas',
      price: 'Desde 280€',
      description: 'Eliminación completa de olores mediante técnicas especializadas de ozonización y neutralización molecular. No enmascaramos los olores, los eliminamos definitivamente mediante procesos químicos y físicos que destruyen las moléculas causantes.',
      includes: [
        'Ozonización profesional del espacio',
        'Aplicación de neutralizadores enzimáticos',
        'Ventilación controlada y forzada',
        'Tratamiento de textiles y superficies porosas',
        'Verificación olfativa final',
        'Garantía de eliminación efectiva'
      ],
      cases: [
        'Olores persistentes tras limpieza inicial',
        'Impregnación en textiles, muebles o paredes',
        'Preparación del inmueble para nuevos ocupantes',
        'Requisito para entrega, venta o alquiler'
      ],
      timing: 'Entre 3 y 6 horas según intensidad del olor y superficie. El espacio debe permanecer cerrado durante el tratamiento de ozonización (2-4 horas) y ventilarse posteriormente.',
      process: [
        'Evaluación de la intensidad y origen del olor',
        'Aplicación de neutralizadores enzimáticos',
        'Ozonización del espacio cerrado',
        'Ventilación controlada y verificación final'
      ]
    },
    {
      id: 'retirada-enseres',
      icon: 'ri-delete-bin-line',
      title: 'Retirada de Enseres',
      subtitle: 'Vaciado completo y gestión de residuos',
      price: 'Desde 600€',
      description: 'Vaciado completo del inmueble y retirada de enseres cuando la situación lo requiere. Realizamos la clasificación, embalaje, transporte y gestión de residuos con la documentación completa de trazabilidad. Coordinamos con la familia para preservar objetos de valor sentimental.',
      includes: [
        'Clasificación de objetos y enseres',
        'Embalaje seguro de elementos a conservar',
        'Transporte autorizado de residuos',
        'Gestión documental de residuos',
        'Coordinación con familia para objetos personales',
        'Limpieza final del espacio vaciado'
      ],
      cases: [
        'Vaciado completo del inmueble tras fallecimiento',
        'Retirada de enseres afectados por la situación',
        'Preparación del inmueble para entrega o venta',
        'Coordinación con herencias y familia'
      ],
      timing: 'Entre 1 y 3 días según volumen de enseres y tamaño del inmueble. Podemos realizar el vaciado por fases si la familia necesita tiempo para revisar objetos.',
      process: [
        'Inventario inicial y coordinación con familia',
        'Clasificación de objetos a conservar, donar o desechar',
        'Embalaje y protección de elementos a preservar',
        'Retirada, transporte y gestión de residuos'
      ]
    },
    {
      id: 'limpieza-choque',
      icon: 'ri-flashlight-line',
      title: 'Limpieza de Choque',
      subtitle: 'Intervención intensiva inmediata',
      price: 'Desde 550€',
      description: 'Intervención intensiva cuando la situación requiere una actuación inmediata y exhaustiva del espacio. Movilizamos un equipo especializado que trabaja de forma intensiva para devolver el inmueble a condiciones óptimas en el menor tiempo posible.',
      includes: [
        'Respuesta inmediata (menos de 4 horas)',
        'Equipo especializado completo',
        'Trabajo intensivo sin interrupciones',
        'Limpieza, desinfección y neutralización integradas',
        'Disponibilidad en fines de semana y festivos',
        'Resultados garantizados'
      ],
      cases: [
        'Situaciones que requieren actuación urgente',
        'Inmuebles con afectación severa',
        'Plazos ajustados para entrega del inmueble',
        'Coordinación urgente con seguros o administraciones'
      ],
      timing: 'Entre 6 y 12 horas de trabajo intensivo continuo. Podemos trabajar en horario nocturno si la situación lo requiere para minimizar molestias.',
      process: [
        'Movilización inmediata del equipo',
        'Evaluación rápida y plan de actuación',
        'Intervención intensiva sin pausas',
        'Verificación exhaustiva y entrega inmediata'
      ]
    },
    {
      id: 'preparacion-entrega',
      icon: 'ri-home-smile-line',
      title: 'Preparación para Entrega',
      subtitle: 'Acondicionamiento final del inmueble',
      price: 'Desde 400€',
      description: 'Acondicionamiento final del inmueble para su entrega, venta o alquiler. Realizamos una limpieza profunda final, coordinamos con otros profesionales si son necesarias reparaciones o pintura, y dejamos el espacio en perfectas condiciones para su nuevo uso.',
      includes: [
        'Limpieza profunda final de todo el inmueble',
        'Coordinación con pintores si es necesario',
        'Gestión de pequeñas reparaciones',
        'Limpieza de cristales y exteriores',
        'Revisión final de instalaciones',
        'Entrega lista para uso inmediato'
      ],
      cases: [
        'Preparación para venta del inmueble',
        'Acondicionamiento para nuevo alquiler',
        'Entrega a herederos o nuevos propietarios',
        'Coordinación con inmobiliarias'
      ],
      timing: 'Entre 1 y 2 días según tamaño del inmueble y trabajos adicionales necesarios. Coordinamos los tiempos con otros profesionales si procede.',
      process: [
        'Evaluación del estado y necesidades adicionales',
        'Coordinación con otros profesionales si procede',
        'Limpieza profunda y acondicionamiento',
        'Revisión final y entrega documentada'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-light to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center space-x-2 bg-primary/10 rounded-full px-4 py-2 mb-6"
          >
            <span className="text-primary font-semibold text-sm">Servicios Especializados</span>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-display font-bold text-5xl lg:text-6xl text-dark mb-6 leading-tight"
          >
            Servicios de <span className="text-primary">limpieza post mortem</span> en Barcelona
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-10"
          >
            Soluciones completas y profesionales para cada situación. Desde la <strong>limpieza tras fallecimiento</strong> hasta la <strong>preparación del inmueble para entrega</strong>, con máxima discreción y protocolos certificados. <Link to="/empresa" className="text-primary hover:underline cursor-pointer">Conozca nuestra empresa y valores</Link>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a
              href="tel:+34629501941"
              className="inline-flex items-center justify-center px-8 py-4 bg-primary text-white font-semibold rounded-full hover:bg-primary/90 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line text-xl mr-3"></i>
              Llamar Ahora
            </a>
            <Link
              to="/contacto"
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-dark text-dark font-semibold rounded-full hover:bg-dark hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Solicitar Presupuesto
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Service Tabs */}
      <section className="sticky top-20 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex overflow-x-auto py-4 scrollbar-hide">
            <div className="flex space-x-4 mx-auto">
              {services.map((service, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setActiveService(index);
                    document.getElementById(service.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className={`px-6 py-3 rounded-full font-medium whitespace-nowrap transition-all duration-300 cursor-pointer ${
                    activeService === index
                      ? 'bg-primary text-white shadow-lg shadow-primary/30'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <i className={`${service.icon} mr-2`}></i>
                  {service.title}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {services.map((service, index) => (
            <div
              key={index}
              id={service.id}
              className="mb-32 last:mb-0 scroll-mt-32"
            >
              <div className="grid lg:grid-cols-3 gap-12">
                {/* Sidebar */}
                <motion.div
                  initial={{ opacity: 0, x: -40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="lg:sticky lg:top-40 lg:self-start"
                >
                  <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-3xl p-8 border border-primary/20">
                    <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center mb-6">
                      <i className={`${service.icon} text-3xl text-white`}></i>
                    </div>
                    
                    <h3 className="font-display font-bold text-2xl text-dark mb-2">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-6">{service.subtitle}</p>
                    
                    <div className="bg-white rounded-2xl p-6 mb-6">
                      <div className="text-sm text-gray-500 mb-1">Precio orientativo</div>
                      <div className="font-display font-bold text-3xl text-primary mb-2">
                        {service.price}
                      </div>
                      <div className="text-xs text-gray-500">
                        * Precio final según valoración específica. <Link to="/faq" className="text-primary hover:underline cursor-pointer">Ver preguntas frecuentes</Link>
                      </div>
                    </div>

                    <h4 className="font-semibold text-dark mb-4">Qué Incluye:</h4>
                    <ul className="space-y-3 mb-8">
                      {service.includes.map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <i className="ri-check-line text-primary text-lg mt-0.5 w-5 h-5 flex items-center justify-center"></i>
                          <span className="text-sm text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to="/contacto"
                      className="flex items-center justify-center w-full px-6 py-4 bg-primary text-white font-semibold rounded-full hover:bg-primary/90 transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      Pedir Presupuesto
                      <i className="ri-arrow-right-line text-xl ml-3"></i>
                    </Link>
                  </div>
                </motion.div>

                {/* Content */}
                <div className="lg:col-span-2 space-y-8">
                  {/* Description */}
                  <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="bg-white border border-gray-200 rounded-3xl p-8"
                  >
                    <h4 className="font-display font-semibold text-2xl text-dark mb-4">
                      Descripción Detallada
                    </h4>
                    <p className="text-gray-600 leading-relaxed text-lg">
                      {service.description}
                    </p>
                  </motion.div>

                  {/* Cases */}
                  <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="bg-white border border-gray-200 rounded-3xl p-8"
                  >
                    <h4 className="font-display font-semibold text-2xl text-dark mb-6">
                      Casos Típicos de Aplicación
                    </h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      {service.cases.map((caseItem, idx) => (
                        <div
                          key={idx}
                          className="flex items-start space-x-3 bg-light rounded-xl p-4"
                        >
                          <i className="ri-checkbox-circle-line text-primary text-xl mt-0.5 w-6 h-6 flex items-center justify-center"></i>
                          <span className="text-gray-700">{caseItem}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  {/* Timing */}
                  <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="bg-white border border-gray-200 rounded-3xl p-8"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className="ri-time-line text-2xl text-primary"></i>
                      </div>
                      <div>
                        <h4 className="font-display font-semibold text-xl text-dark mb-3">
                          Tiempos Orientativos
                        </h4>
                        <p className="text-gray-600 leading-relaxed">
                          {service.timing}
                        </p>
                      </div>
                    </div>
                  </motion.div>

                  {/* Process */}
                  <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="bg-white border border-gray-200 rounded-3xl p-8"
                  >
                    <h4 className="font-display font-semibold text-2xl text-dark mb-6">
                      Proceso Específico
                    </h4>
                    <div className="space-y-4">
                      {service.process.map((step, idx) => (
                        <div key={idx} className="flex items-start space-x-4">
                          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="font-bold text-white">{idx + 1}</span>
                          </div>
                          <div className="flex-1 pt-2">
                            <p className="text-gray-700">{step}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  {/* Image */}
                  <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="rounded-3xl overflow-hidden"
                  >
                    <img
                      src={
                        index === 0 ? 'https://public.readdy.ai/ai/img_res/bf6f21b0b03cb572ceab1f2a785e739a.jpg' :
                        index === 1 ? 'https://readdy.ai/api/search-image?query=disinfection%20spray%20worker&width=1200&height=600&seq=srv2&orientation=landscape' :
                        index === 2 ? 'https://readdy.ai/api/search-image?query=ozone%20generator%20machine&width=1200&height=600&seq=srv3&orientation=landscape' :
                        index === 3 ? 'https://readdy.ai/api/search-image?query=furniture%20removal%20service&width=1200&height=600&seq=srv4&orientation=landscape' :
                        index === 4 ? 'https://readdy.ai/api/search-image?query=emergency%20cleaning%20team&width=1200&height=600&seq=srv5&orientation=landscape' :
                        'https://readdy.ai/api/search-image?query=clean%20empty%20apartment&width=1200&height=600&seq=srv6&orientation=landscape'
                      }
                      alt={service.title}
                      className="w-full h-80 object-cover object-top"
                    />
                  </motion.div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 lg:py-32 bg-light">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-display font-bold text-4xl lg:text-5xl text-dark mb-6"
            >
              Comparativa de Servicios
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-xl text-gray-600 max-w-3xl mx-auto"
            >
              Encuentre el servicio que mejor se adapta a sus necesidades. <Link to="/contacto" className="text-primary hover:underline cursor-pointer">Solicite una valoración personalizada</Link>.
            </motion.p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-2xl overflow-hidden shadow-lg">
              <thead className="bg-dark text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold">Característica</th>
                  <th className="px-6 py-4 text-center font-semibold">Limpieza Post Mortem</th>
                  <th className="px-6 py-4 text-center font-semibold">Desinfección</th>
                  <th className="px-6 py-4 text-center font-semibold">Neutralización</th>
                  <th className="px-6 py-4 text-center font-semibold">Retirada Enseres</th>
                  <th className="px-6 py-4 text-center font-semibold">Limpieza Choque</th>
                  <th className="px-6 py-4 text-center font-semibold">Preparación</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr className="hover:bg-light transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-700">Limpieza Profunda</td>
                  <td className="px-6 py-4 text-center"><i className="ri-check-line text-primary text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300 text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300 text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-check-line text-primary text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-check-line text-primary text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-check-line text-primary text-2xl"></i></td>
                </tr>
                <tr className="hover:bg-light transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-700">Desinfección</td>
                  <td className="px-6 py-4 text-center"><i className="ri-check-line text-primary text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-check-line text-primary text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300 text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300 text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-check-line text-primary text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-check-line text-primary text-2xl"></i></td>
                </tr>
                <tr className="hover:bg-light transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-700">Neutralización Olores</td>
                  <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300 text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300 text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-check-line text-primary text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300 text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-check-line text-primary text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300 text-2xl"></i></td>
                </tr>
                <tr className="hover:bg-light transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-700">Vaciado Inmueble</td>
                  <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300 text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300 text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300 text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-check-line text-primary text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300 text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300 text-2xl"></i></td>
                </tr>
                <tr className="hover:bg-light transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-700">Certificado Oficial</td>
                  <td className="px-6 py-4 text-center"><i className="ri-check-line text-primary text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-check-line text-primary text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-check-line text-primary text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-check-line text-primary text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-check-line text-primary text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-check-line text-primary text-2xl"></i></td>
                </tr>
                <tr className="hover:bg-light transition-colors">
                  <td className="px-6 py-4 font-medium text-gray-700">Respuesta Urgente</td>
                  <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300 text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300 text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300 text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300 text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-check-line text-primary text-2xl"></i></td>
                  <td className="px-6 py-4 text-center"><i className="ri-close-line text-gray-300 text-2xl"></i></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-dark text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-display font-bold text-4xl lg:text-5xl text-white mb-6"
          >
            ¿Necesita un presupuesto sin compromiso?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto"
          >
            Contacte con nosotros para una valoración personalizada sin compromiso. Le atenderemos de inmediato y resolveremos todas sus dudas. <Link to="/faq" className="text-primary hover:underline cursor-pointer">Consulte nuestras preguntas frecuentes</Link> o <Link to="/empresa" className="text-primary hover:underline cursor-pointer">conozca más sobre nuestra empresa</Link>.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <a
              href="tel:+34629501941"
              className="inline-flex items-center justify-center px-8 py-4 bg-primary text-white font-semibold rounded-full hover:bg-primary/90 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line text-xl mr-3"></i>
              +34 629 501 941
            </a>
            <a
              href="https://wa.me/34629501941?text=Hola,%20necesito%20información%20sobre%20sus%20servicios"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-dark font-semibold rounded-full hover:bg-gray-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-xl mr-3"></i>
              WhatsApp
            </a>
            <Link
              to="/contacto"
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-dark transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Formulario
              <i className="ri-arrow-right-line text-xl ml-3"></i>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
      <CookieBanner />
    </div>
  );
};

export default ServiciosPage;
